/**
 * Mantine v9 date pickers emit `"YYYY-MM-DD HH:mm:ss"` strings (not Date
 * objects), but every backend datetime field is Zod `z.string().datetime()`
 * — strict ISO-8601 with a Z suffix. Sending the picker value verbatim is
 * rejected with "Invalid start time"-class 400s (SCRUM-121); untouched
 * defaults pass only because initialValues are already ISO strings.
 *
 * dayjs (not `new Date(...)`) does the parsing because Safari cannot parse
 * the space-separated format at all.
 */
import dayjs from "dayjs";

/**
 * Convert a Mantine picker value (DateStringValue, Date, or already-ISO
 * string) to the strict ISO string the backend accepts. The picker value is
 * the user's local wall-clock time, so the instant conversion is correct.
 * Returns null for empty/unparseable input.
 */
export function toIsoDateTime(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * Convert a Mantine picker value to a strict date-only `"YYYY-MM-DD"` string —
 * the shape the backend's `z.string().date()` fields require. Sending a full ISO
 * datetime to a `.date()` field is rejected ("Invalid ... date"); sending the raw
 * picker value fails for the same Safari reason as {@link toIsoDateTime}. Uses
 * dayjs (not `new Date(...)`) so the space-separated picker format parses on
 * Safari. Returns null for empty/unparseable input.
 */
export function toDateOnly(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

/** The wire format Mantine v9 pickers both emit and render. */
const PICKER_FORMAT = "YYYY-MM-DD HH:mm:ss";

/**
 * Move an end datetime so it keeps its distance from a start datetime that the
 * user just changed.
 *
 * Without this, changing only the start date of an event leaves the end on the
 * old date — so picking a start two days out silently produces an inverted
 * range that the form then rejects, and the user has to fix a second field they
 * did not touch. Shifting by the same delta preserves the duration they already
 * chose, which is what calendar UIs conventionally do.
 *
 * Returns the new end in the picker's own format (NOT ISO) so the value can go
 * straight back into the picker and render as the same wall-clock time; ISO
 * conversion still happens once, at submit, via `dateTimeFields`.
 *
 * Returns null when any input is missing or unparseable, or when the end is not
 * after the start — in that case the range was already invalid and preserving
 * it is meaningless, so the caller should leave the end alone and let
 * validation speak.
 */
export function shiftEndPreservingDuration(
  previousStart: string | Date | null | undefined,
  nextStart: string | Date | null | undefined,
  currentEnd: string | Date | null | undefined,
): string | null {
  if (!previousStart || !nextStart || !currentEnd) return null;
  const prev = dayjs(previousStart);
  const next = dayjs(nextStart);
  const end = dayjs(currentEnd);
  if (!prev.isValid() || !next.isValid() || !end.isValid()) return null;

  const durationMs = end.diff(prev);
  if (durationMs <= 0) return null;

  return next.add(durationMs, "millisecond").format(PICKER_FORMAT);
}
